import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

const ISSUE_CATEGORY_ORDER = ['Critical', 'Major', 'Minor', 'Edge Case', 'Silly/Basic', 'Suggestions'];

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const getEntries = (issuesByCategory) => {
  const obj = issuesByCategory && typeof issuesByCategory === 'object' ? issuesByCategory : {};

  const fromPreferredOrder = ISSUE_CATEGORY_ORDER.map((name) => [name, toNumber(obj[name])]);
  const extras = Object.keys(obj)
    .filter((k) => !ISSUE_CATEGORY_ORDER.includes(k))
    .map((k) => [k, toNumber(obj[k])]);

  return [...fromPreferredOrder, ...extras].filter(([, count]) => count > 0).sort((a, b) => b[1] - a[1]);
};

export default function IssueBreakdownDialog({ open, onClose, developerName, totalIssues, issuesByCategory }) {
  const entries = getEntries(issuesByCategory);
  const computedSum = entries.reduce((acc, [, n]) => acc + n, 0);
  const total = toNumber(totalIssues) || computedSum;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      {/* HEADER */}
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle1" fontWeight={600}>
            Issue breakdown
            {developerName ? ` • ${developerName}` : ''}
          </Typography>

          <Typography variant="h5" fontWeight={700}>
            {total}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No issues in this date range.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {entries.map(([name, count]) => {
              const isSilly = name === 'Silly/Basic';

              return (
                <Stack
                  key={name}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 1,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                  }}
                >
                  <Chip
                    size="small"
                    label={name}
                    color={isSilly ? 'error' : 'default'}
                    variant={isSilly ? 'outlined' : 'filled'}
                    sx={{
                      fontWeight: 500,
                      height: 24,
                    }}
                  />

                  <Typography variant="body2" fontWeight={600}>
                    {count}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        )}

        {/* NOTE */}
        {toNumber(totalIssues) > 0 && computedSum > 0 && toNumber(totalIssues) !== computedSum && (
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1.5, display: 'block' }}>
            Category total ({computedSum}) differs from overall total ({toNumber(totalIssues)})
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={onClose} variant="contained" fullWidth>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

IssueBreakdownDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  developerName: PropTypes.string,
  dateRangeLabel: PropTypes.string,
  totalIssues: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  issuesByCategory: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
};

IssueBreakdownDialog.defaultProps = {
  developerName: '',
  dateRangeLabel: '',
  totalIssues: 0,
  issuesByCategory: {},
};
